import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Venue } from "shared/assets/icons";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { StageCapacityForm } from "Venues/components/StageCapacity/components/StageCapacityForm";
import { createStageCapacity, dataMetaSelector, dispatchGetVenueAction } from "Venues/state";
import { IStageCapacity, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

const venueBlockCopy = copy.venueBlock.stageCapacity;

interface CreateStageCapacityModalProps {
  venueId: string;
  stageId: string;
  handleClose: () => void;
  onCreateSuccess: (capacityId: string) => void;
}

export const CreateStageCapacityModal = ({
  venueId,
  stageId,
  handleClose,
  onCreateSuccess,
}: CreateStageCapacityModalProps) => {
  const dispatch = useDispatch();
  const { api: apiMeta, id: createdCapacityId } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.createStageCapacity)
  );
  useClearActionMetaOnUnMount(VENUE_ACTIONS.createStageCapacity);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && createdCapacityId) {
      dispatch(
        addTemporaryToast({
          message: venueBlockCopy.messages.capacityAdded,
          status: IStatus.success,
        })
      );

      // fetch venue to update
      dispatchGetVenueAction(dispatch, venueId);

      handleClose();

      onCreateSuccess(createdCapacityId);
    }
  }, [apiMeta?.status, dispatch, handleClose, venueId, createdCapacityId, onCreateSuccess]);

  const handleSubmit = (capacity: IStageCapacity) => {
    const stagePath = API_ENDPOINTS.STAGE(stageId);

    dispatch(createStageCapacity({ stagePath, capacity }));
  };

  const buttons = useMemo(
    (): IFormButtons => ({
      primary: {
        "data-testid": "create_stage",
        children: venueBlockCopy.buttons.createCapacity,
        size: "m",
      },
      secondary: {
        onClick: handleClose,
        size: "m",
      },
    }),
    [handleClose]
  );

  return (
    <Modal title={venueBlockCopy.titles.createCapacity} icon={Venue} handleClose={handleClose}>
      <StageCapacityForm
        isSubmitting={apiMeta?.status === IAPIState.pending}
        hasError={!!apiMeta?.error}
        handleSubmit={handleSubmit}
        buttons={buttons}
      />
    </Modal>
  );
};
